import {State} from "../state";
import type {BookingflowStateMachine} from "../../bookingflowstatemachine";
import type {DetailsStateDependencies} from "../detailsState/detailsState.types";
import {IGetPackageStateData, packActions, packageActions, PackageStateDependencies} from "./packageState.types";
import type {PackageAction} from "./packageState.types";
import DetailsState from "../detailsState";
import { Package, BasisType, Address, Customer, Goal, Service, Partner, PackageTemplate, CustomerType } from "@raskrask-dk/raskrask-utils";

const PACKAGE_STATE = "PACKAGE";
export default class PackageState extends State<PackageAction, typeof PACKAGE_STATE> {
  public readonly type = PACKAGE_STATE;

  private _package: Package | undefined;
  private _basisType: BasisType;
  private _address: Address;
  private _customerType: Customer["type"];
  private _goal: Goal | undefined;
  private _service: Service | undefined;
  private _partner: Partner | undefined;
  private _packageTemplate: PackageTemplate | undefined;

  constructor(bookingflowStateMachine: BookingflowStateMachine, dependencies: PackageStateDependencies) {
    super(bookingflowStateMachine);
    const {address, basisType, customerType, goal, service, partner, packageTemplate} = dependencies;
    this._address = address;
    this._basisType = basisType;
    this._customerType = customerType;
    this._goal = goal;
    this._service = service;
    this._partner = partner;
    this._packageTemplate = packageTemplate;
  }

  private nextState(): DetailsState {
    return new DetailsState(this.bookingflowStateMachine, this.detailsDeps);
  }

  doAction(action: PackageAction): PackageState | DetailsState {
    switch (action.type) {
      case "SET_GOAL":
        this._goal = action.data;
        return this;
      case "SET_SERVICE":
        this._service = action.data;
        return this;
      case "SET_PARTNER":
        this._partner = action.data;
        return this;
      case "SET_PACKAGETEMPLATE":
        this._packageTemplate = action.data;
        return this;
      case "NEXT_STATE":
        this._package =
          this._packageTemplate !== undefined && this._partner !== undefined
            ? Package.fromPackageTemplate(this._packageTemplate, this._partner)
            : undefined;
        return this.nextState();
      default:
        throw new Error("These are not the droids you're looking for");
    }
  }

  public getData(): IGetPackageStateData {
    return {
      package: this.package,
      basisType: this.basisType,
      address: this.address,
      customerType: this.customerType,
      goal: this.goal,
      service: this.service,
      partner: this.partner,
      packageTemplate: this.packageTemplate,
    };
  }

  public unset = (action: PackageAction): void => {
    /* switch (action.type) {
      case "SET_MULTIPLIER":
        this._partner = this._partner;
        break;
      case "SET_GOAL":
        this._partner = undefined;
    } */
  };

  public getActions = (): packActions[] => packageActions.filter((action) => new action(this).isDoable());

  public get service(): Service | undefined {
    return this._service;
  }

  public get packageTemplate(): PackageTemplate | undefined {
    return this._packageTemplate;
  }

  public get basisType(): BasisType {
    return this._basisType;
  }

  public get goal(): Goal | undefined {
    return this._goal;
  }

  public get package(): Package | undefined {
    return this._package;
  }

  public get address(): Address {
    return this._address;
  }

  public get customerType(): CustomerType {
    return this._customerType;
  }

  public get partner(): Partner | undefined {
    return this._partner;
  }

  private get detailsDeps(): DetailsStateDependencies {
    if (this._package) {
      return {
        product: this._package,
        address: this._address,
        customerType: this._customerType,
        ...(this.bookingflowStateMachine.suggestiveChoices !== "EMPTY" && this.bookingflowStateMachine.suggestiveChoices),
      };
    }
    throw new Error("Can not retrive detailsDeps, until a package has not been selected.");
  }
}
